/**
 * narrativeApi.mock.ts — offline fixtures for the narrative EDIT endpoints,
 * used when REACT_APP_STORY_CANVAS_API === "mock". Lets the improve / edit /
 * generate UI work end-to-end before the backend branch merges.
 *
 * Each mock persists its text into the canvas mock store (storyCanvasApi.mock)
 * before responding, so the post-save widget refetch re-renders the edited
 * summary/narrative exactly as it will against the live API.
 */
import type {
  CanvasItemNarrativeManualUpdateRequest,
  CanvasItemSummaryAcceptedResponse,
  CanvasItemSummaryManualUpdateRequest,
  CanvasItemSummaryResponse,
  StorySummaryAcceptedResponse,
  StorySummaryManualUpdateRequest,
  StorySummaryResponse,
  CanvasItemNarrativeResponse,
  NarrativeImproveRequest,
  StoryNarrativeManualUpdateRequest,
  StoryNarrativeResponse,
} from "./narrativeApi.types";
import {
  applyMockCanvasItemNarrative,
  applyMockCanvasItemSummary,
  applyMockStoryNarrative,
  clearMockCanvasItemNarrative,
  MOCK_STORY_NARRATIVE_WIDGET_ID,
} from "../storyCanvas/storyCanvasApi.mock";

/** Constant timestamp keeps snapshot tests deterministic. */
const MOCK_TIMESTAMP = "2026-01-01T00:00:00.000Z";

// ─── Story-level narrative ────────────────────────────────────────────────────
// Each mock writes into the canvas mock store before responding, so the widget
// refetch that useNarrativeEdit triggers on success returns the same text —
// the exact round-trip the live API performs. Narrative actions persist ONLY
// the narrative section — the summary section belongs to the /summary family.

export function mockGenerateStoryNarrative(
  storyId: string,
): StoryNarrativeResponse {
  const narrative =
    "This story's key movements, generated from the underlying metrics.";
  applyMockStoryNarrative({ narrative });
  return {
    story_id: storyId,
    item_id: MOCK_STORY_NARRATIVE_WIDGET_ID,
    summary: "AI narrative",
    narrative,
    source: "ai_generated",
    generated_at: MOCK_TIMESTAMP,
    updated_at: MOCK_TIMESTAMP,
  };
}

export function mockImproveStoryNarrative(
  storyId: string,
  body?: NarrativeImproveRequest,
): StoryNarrativeResponse {
  const steer = body?.instructions ? ` (${body.instructions})` : "";
  const narrative = `A tighter, improved narrative${steer}.`;
  applyMockStoryNarrative({ narrative });
  return {
    story_id: storyId,
    item_id: MOCK_STORY_NARRATIVE_WIDGET_ID,
    summary: "AI narrative",
    narrative,
    source: "ai_improved",
    generated_at: MOCK_TIMESTAMP,
    updated_at: MOCK_TIMESTAMP,
  };
}

export function mockUpdateStoryNarrative(
  storyId: string,
  body: StoryNarrativeManualUpdateRequest,
): StoryNarrativeResponse {
  // Only the narrative section persists — `summary` here is the narrative
  // item's title, NOT the story card's summary section (that section belongs
  // to the /summary family and must survive narrative edits).
  applyMockStoryNarrative({ narrative: body.narrative });
  return {
    story_id: storyId,
    item_id: MOCK_STORY_NARRATIVE_WIDGET_ID,
    summary: body.summary ?? null,
    narrative: body.narrative,
    source: "manual",
    generated_at: MOCK_TIMESTAMP,
    updated_at: MOCK_TIMESTAMP,
  };
}

// ─── Canvas-item (chart/kpi) ────────────────────────────────────────────────

export function mockGenerateCanvasItemNarrative(
  storyId: string,
  itemId: string,
): CanvasItemNarrativeResponse {
  const narrative = "AI-generated narrative for this chart.";
  applyMockCanvasItemNarrative(itemId, narrative);
  return {
    story_id: storyId,
    item_id: itemId,
    narrative,
    source: "ai_generated",
    generated_at: MOCK_TIMESTAMP,
    updated_at: MOCK_TIMESTAMP,
  };
}

export function mockImproveCanvasItemNarrative(
  storyId: string,
  itemId: string,
  body?: NarrativeImproveRequest,
): CanvasItemNarrativeResponse {
  const steer = body?.instructions ? ` (${body.instructions})` : "";
  const narrative = `An improved narrative for this chart${steer}.`;
  applyMockCanvasItemNarrative(itemId, narrative);
  return {
    story_id: storyId,
    item_id: itemId,
    narrative,
    source: "ai_improved",
    generated_at: MOCK_TIMESTAMP,
    updated_at: MOCK_TIMESTAMP,
  };
}

export function mockDeleteCanvasItemNarrative(itemId: string): void {
  clearMockCanvasItemNarrative(itemId);
}

export function mockUpdateCanvasItemNarrative(
  storyId: string,
  itemId: string,
  body: CanvasItemNarrativeManualUpdateRequest,
): CanvasItemNarrativeResponse {
  applyMockCanvasItemNarrative(itemId, body.narrative);
  return {
    story_id: storyId,
    item_id: itemId,
    narrative: body.narrative,
    source: "manual",
    generated_at: MOCK_TIMESTAMP,
    updated_at: MOCK_TIMESTAMP,
  };
}

// ─── Story-level summary ─────────────────────────────────────────────────────
// Persists ONLY the summary section — the narrative section must never change
// from a summary action (and vice versa).

export function mockGenerateStorySummary(storyId: string): StorySummaryResponse {
  const text = "AI-generated story summary.";
  applyMockStoryNarrative({ summary: text });
  return {
    story_id: storyId,
    canvas_item_id: `${storyId}-summary`,
    status: "confirmed",
    text,
    version: 1,
    source: "ai_generated",
    generated_at: MOCK_TIMESTAMP,
  };
}

export function mockImproveStorySummary(
  storyId: string,
  body?: NarrativeImproveRequest,
): StorySummaryAcceptedResponse {
  const steer = body?.instructions ? ` (${body.instructions})` : "";
  applyMockStoryNarrative({ summary: `An improved story summary${steer}.` });
  return { story_id: storyId, status: "generating" };
}

export function mockUpdateStorySummary(
  storyId: string,
  body: StorySummaryManualUpdateRequest,
): StorySummaryResponse {
  applyMockStoryNarrative({ summary: body.text });
  return {
    story_id: storyId,
    canvas_item_id: `${storyId}-summary`,
    status: "confirmed",
    text: body.text,
    version: 2,
    source: "manual",
    generated_at: MOCK_TIMESTAMP,
  };
}

// ─── Canvas-item (chart/kpi) summary ─────────────────────────────────────────
// generate/override answer synchronously with the saved text; improve mirrors
// the live 202 contract (accepted stub) but persists the improved text at once,
// simulating an instantly-completed pipeline so the widget refetch shows it.

export function mockGenerateCanvasItemSummary(
  storyId: string,
  itemId: string,
): CanvasItemSummaryResponse {
  const text = "AI-generated summary for this chart.";
  applyMockCanvasItemSummary(itemId, text);
  return {
    story_id: storyId,
    item_id: itemId,
    canvas_item_id: `${itemId}-summary`,
    status: "confirmed",
    text,
    version: 1,
    source: "ai_generated",
    generated_at: MOCK_TIMESTAMP,
    updated_at: MOCK_TIMESTAMP,
  };
}

export function mockImproveCanvasItemSummary(
  itemId: string,
  body?: NarrativeImproveRequest,
): CanvasItemSummaryAcceptedResponse {
  const steer = body?.instructions ? ` (${body.instructions})` : "";
  applyMockCanvasItemSummary(itemId, `An improved summary for this chart${steer}.`);
  return { item_id: itemId, status: "generating" };
}

export function mockUpdateCanvasItemSummary(
  storyId: string,
  itemId: string,
  body: CanvasItemSummaryManualUpdateRequest,
): CanvasItemSummaryResponse {
  applyMockCanvasItemSummary(itemId, body.text);
  return {
    story_id: storyId,
    item_id: itemId,
    canvas_item_id: `${itemId}-summary`,
    status: "confirmed",
    text: body.text,
    version: 2,
    source: "manual",
    generated_at: MOCK_TIMESTAMP,
    updated_at: MOCK_TIMESTAMP,
  };
}
